import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../services/apiClient';

// ── Generic interfaces (preserved exactly) ────────────────────────────────────
export interface ApiAutocompleteOption {
  [key: string]: any;
}

export interface ApiResponse<T> {
  status?: string;
  message?: string;
  data: T[] | {
    data: T[];
    meta?: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    links?: any;
  };
}

export interface ApiAutocompleteProps<T extends ApiAutocompleteOption> {
  // API Configuration
  endpoint: string;
  searchParamName?: string;
  additionalParams?: Record<string, any>;

  // Data Configuration
  labelKey: keyof T | ((option: T) => string);
  valueKey: keyof T;
  secondaryLabelKey?: keyof T | ((option: T) => string);

  // Component Props
  value?: T | T[] | null;
  onChange: (value: T | T[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;

  // Custom Rendering
  renderOption?: (props: any, option: T, state: any) => React.ReactNode;
  renderTags?: (value: T[], getTagProps: any) => React.ReactNode;
  renderInput?: (params: any) => React.ReactNode;

  // Behavior Configuration
  minSearchLength?: number;
  debounceMs?: number;
  pageSize?: number;
  enablePagination?: boolean;

  // React Query Options
  queryKey?: string[];
  staleTime?: number;
  cacheTime?: number;

  // Pass-through props (no-op in native impl, kept for API compat)
  textFieldProps?: Record<string, any>;
  autocompleteProps?: Record<string, any>;
}

// ── Simple debounce (no MUI dependency) ──────────────────────────────────────
function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ── Component ─────────────────────────────────────────────────────────────────
export const ApiAutocomplete = <T extends ApiAutocompleteOption>({
  endpoint,
  searchParamName = '_q',
  additionalParams = {},
  labelKey,
  valueKey,
  secondaryLabelKey,
  value,
  onChange,
  multiple = false,
  disabled = false,
  error = false,
  helperText,
  placeholder,
  label,
  required = false,
  renderOption,
  renderTags,
  minSearchLength = 0,
  debounceMs = 300,
  pageSize = 15,
  enablePagination = false,
  queryKey,
  staleTime = 300000,
  cacheTime = 600000,
}: ApiAutocompleteProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(inputValue, debounceMs);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getLabel = useCallback((option: T): string => {
    if (typeof labelKey === 'function') return labelKey(option);
    return String(option[labelKey] || '');
  }, [labelKey]);

  const getSecondaryLabel = useCallback((option: T): string => {
    if (!secondaryLabelKey) return '';
    if (typeof secondaryLabelKey === 'function') return secondaryLabelKey(option);
    return String(option[secondaryLabelKey] || '');
  }, [secondaryLabelKey]);

  const getValue = useCallback((option: T): any => option[valueKey], [valueKey]);

  // ── Query params ──────────────────────────────────────────────────────────
  const queryParams = useMemo(() => {
    const params: Record<string, any> = { ...additionalParams };
    if (debouncedSearch && debouncedSearch.length >= minSearchLength) {
      params[searchParamName] = debouncedSearch;
    }
    if (enablePagination) {
      params.page = 1;
      params.per_page = pageSize;
    }
    return params;
  }, [debouncedSearch, additionalParams, searchParamName, minSearchLength, enablePagination, pageSize]);

  // ── Data fetch ────────────────────────────────────────────────────────────
  const { data: apiResponse, isLoading, isError } = useQuery({
    queryKey: queryKey || [endpoint, queryParams],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<T>>(endpoint, queryParams);
      return response;
    },
    staleTime,
    gcTime: cacheTime,
    enabled: !disabled,
  });

  const options = useMemo(() => {
    if (!apiResponse?.data) return [];
    const rd = apiResponse.data;
    if (Array.isArray(rd)) return rd;
    if (rd.data && Array.isArray(rd.data)) return rd.data;
    return [];
  }, [apiResponse]);

  // ── Selection helpers ─────────────────────────────────────────────────────
  const selectedValues: T[] = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  const isSelected = useCallback((option: T) => {
    return selectedValues.some((v) => getValue(v) === getValue(option));
  }, [selectedValues, getValue]);

  const handleSelect = useCallback((option: T) => {
    if (multiple) {
      const already = isSelected(option);
      const next = already
        ? selectedValues.filter((v) => getValue(v) !== getValue(option))
        : [...selectedValues, option];
      onChange(next.length ? next : null);
    } else {
      onChange(option);
      setInputValue(getLabel(option));
      setOpen(false);
    }
  }, [multiple, isSelected, selectedValues, getValue, onChange, getLabel]);

  const handleRemoveTag = useCallback((option: T) => {
    if (!multiple) return;
    const next = selectedValues.filter((v) => getValue(v) !== getValue(option));
    onChange(next.length ? next : null);
  }, [multiple, selectedValues, getValue, onChange]);

  // Sync input for single-value mode
  useEffect(() => {
    if (!multiple && value && !Array.isArray(value)) {
      setInputValue(getLabel(value as T));
    } else if (!multiple && !value) {
      setInputValue('');
    }
  }, [value, multiple, getLabel]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── No-options text ───────────────────────────────────────────────────────
  const noOptionsText = isError
    ? 'Error loading options'
    : debouncedSearch.length > 0 && debouncedSearch.length < minSearchLength
      ? `Type at least ${minSearchLength} characters`
      : 'No options found';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {label && (
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
          {label}{required && <span style={{ color: 'var(--err)', marginLeft: 2 }}>*</span>}
        </label>
      )}

      {/* Tags for multiple mode */}
      {multiple && selectedValues.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {renderTags
            ? renderTags(selectedValues, ({ index }: { index: number }) => ({
                onDelete: () => handleRemoveTag(selectedValues[index]),
              }))
            : selectedValues.map((opt) => (
                <span
                  key={getValue(opt)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 4,
                    background: 'rgba(59,130,246,0.1)', color: 'var(--brand-blue)',
                    fontSize: 12, border: '1px solid rgba(59,130,246,0.2)',
                  }}
                >
                  {getLabel(opt)}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(opt)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', lineHeight: 1 }}
                    aria-label={`Remove ${getLabel(opt)}`}
                  >
                    ×
                  </button>
                </span>
              ))}
        </div>
      )}

      {/* Input */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          className={`input${error ? ' input-error' : ''}`}
          value={inputValue}
          placeholder={disabled ? (multiple ? 'Select a parent first...' : placeholder) : placeholder}
          disabled={disabled}
          required={required && !selectedValues.length}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          style={{ paddingRight: 32 }}
          autoComplete="off"
        />
        {/* Spinner or chevron */}
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          pointerEvents: 'none', color: 'var(--ink-3)', fontSize: 12,
        }}>
          {isLoading ? '…' : '▾'}
        </span>
      </div>

      {helperText && (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: error ? 'var(--err)' : 'var(--ink-3)' }}>
          {helperText}
        </p>
      )}

      {/* Dropdown */}
      {open && !disabled && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
            marginTop: 2, background: 'var(--bg-elev)',
            border: '1px solid var(--line)', borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            maxHeight: 240, overflowY: 'auto',
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--ink-3)' }}>
              {isLoading ? 'Loading...' : noOptionsText}
            </div>
          ) : (
            options.map((option) => {
              const selected = isSelected(option);
              const primaryLabel = getLabel(option);
              const secondaryLabel = getSecondaryLabel(option);
              const optKey = getValue(option);

              if (renderOption) {
                return (
                  <div key={optKey} onClick={() => handleSelect(option)} style={{ cursor: 'pointer' }}>
                    {renderOption({ onClick: () => handleSelect(option) }, option, { selected })}
                  </div>
                );
              }

              return (
                <button
                  key={optKey}
                  type="button"
                  onClick={() => handleSelect(option)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 14px', border: 'none', cursor: 'pointer',
                    background: selected ? 'rgba(59,130,246,0.08)' : 'transparent',
                    color: selected ? 'var(--brand-blue)' : 'var(--ink)',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: selected ? 600 : 400 }}>{primaryLabel}</div>
                  {secondaryLabel && (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>{secondaryLabel}</div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ApiAutocomplete;
